/**
 * Snake Snacks — reveal-on-scroll for product cards and promo cards (styles in snake-motion.css).
 * snippets/snake-head.liquid adds html.snk-reveal before first paint (and removes it again after
 * 2.5s if this script never runs), so content can't get stuck hidden.
 *
 * Pages marked data-motion="lr" (category pages, where you browse sideways through categories and
 * filters) bring cards in from the left, column by column, left to right.
 */
if (document.documentElement.classList.contains('snk-reveal')) {
  const sideways = (/** @type {Element} */ el) => !!el.closest('[data-motion="lr"]');

  // cards that appear together cascade in reading order; on sideways pages the columns lead
  const cascade = (/** @type {HTMLElement[]} */ els, base = 0) => {
    const items = els
      .map((el) => ({ el, r: el.getBoundingClientRect() }))
      .sort((a, b) => a.r.top - b.r.top || a.r.left - b.r.left);
    let row = -1;
    let col = 0;
    let top = -Infinity;
    items.forEach(({ el, r }, i) => {
      if (Math.abs(r.top - top) > 4) {
        row++;
        col = 0;
        top = r.top;
      } else {
        col++;
      }
      const ms = sideways(el) ? Math.min(col * 90 + row * 40, 520) : Math.min(i, 6) * 45;
      el.style.setProperty('--snk-delay', `${base + ms}ms`);
    });
  };

  const io = new IntersectionObserver(
    (entries) => {
      const shown = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => /** @type {HTMLElement} */ (entry.target))
        .filter((el) => !el.classList.contains('is-in'));
      cascade(shown);
      shown.forEach((el) => {
        el.classList.add('is-in');
        io.unobserve(el);
      });
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
  );

  const watch = (root) => {
    root.querySelectorAll?.('.snc:not(.is-in), .snk-promo:not(.is-in)').forEach((el) => io.observe(el));
  };
  watch(document);

  // cards added later (filters loading more pages, recommendations, search results)
  new MutationObserver((mutations) => {
    mutations.forEach((m) =>
      m.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches('.snc, .snk-promo')) n.classList.contains('is-in') || io.observe(n);
        else watch(n);
      })
    );
  }).observe(document.body, { childList: true, subtree: true });

  // once a card has arrived it stays put: re-ordering the grid in the background mustn't replay it
  document.addEventListener('animationend', (e) => {
    const el = /** @type {HTMLElement} */ (e.target);
    if (el.classList?.contains('snc') && el.classList.contains('is-in')) el.classList.add('is-done');
  });

  window.SnakeReveal = {
    /**
     * After the shopper filters or sorts: cards on screen come in again right away,
     * the rest when they're scrolled to.
     * @param {HTMLElement} root
     * @param {{ base?: number }} [options] extra delay in ms (e.g. to follow the page header in)
     */
    replay(root, { base = 0 } = {}) {
      const cards = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('.snc')]).filter((el) => !el.hidden);
      /** @type {HTMLElement[]} */
      const onScreen = [];
      cards.forEach((el) => {
        el.classList.remove('is-in', 'is-done');
        const r = el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight) {
          io.unobserve(el);
          onScreen.push(el);
        } else {
          io.observe(el);
        }
      });
      // let the cards drop back to hidden first, so their entrance starts over
      void root.offsetWidth;
      cascade(onScreen, base);
      onScreen.forEach((el) => el.classList.add('is-in'));
    },
  };
}
